import { useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useTRPC } from "@/trpc";
import type { RouterOutputs } from "@/trpc";

export type TermsStatus =
  RouterOutputs["organization"]["getUserOrganizations"][number]["termsAcceptance"];

export function OrganizationTermsAgreement({
  status,
  agreement,
}: {
  status: TermsStatus;
  agreement: NonNullable<TermsStatus["agreement"]>;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const checkboxId = useId();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const queryKey = trpc.organization.getUserOrganizations.queryKey();
  const acceptMutation = useMutation(
    trpc.organization.acceptTerms.mutationOptions({
      onSuccess: (acceptance, input) => {
        queryClient.setQueryData(queryKey, (organizations) =>
          organizations?.map((organization) => {
            const previous = organization.termsAcceptance;
            if (
              organization.id !== input.organizationId ||
              previous?.agreement?.id !== acceptance.agreementId ||
              previous.agreement.version !== acceptance.termsVersion ||
              previous.agreement.url !== acceptance.termsUrl
            ) {
              return organization;
            }
            return {
              ...organization,
              termsAcceptance: { ...previous, acceptance },
            };
          }),
        );
      },
      // A rejected revision must be fetched again before the administrator retries.
      onSettled: () => queryClient.invalidateQueries({ queryKey }),
    }),
  );

  function acceptTerms(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!confirmed || !status.canAccept || acceptMutation.isPending) {
      return;
    }

    acceptMutation.mutate({
      organizationId: status.organizationId,
      agreementId: agreement.id,
      termsVersion: agreement.version,
      termsUrl: agreement.url,
      accepted: true,
      authorizedToAccept: true,
    });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <a
          href={agreement.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary underline underline-offset-4"
        >
          Read the organization Terms of Service
        </a>
        <p className="text-sm text-muted-foreground">
          Agreement version {agreement.version}
        </p>
      </div>

      {status.acceptance && (
        <p role="status" className="text-sm">
          Accepted for {status.organizationName} by{" "}
          {status.acceptance.acceptedByName} on{" "}
          <time dateTime={status.acceptance.acceptedAt}>
            {Temporal.Instant.from(status.acceptance.acceptedAt)
              .toZonedDateTimeISO(Temporal.Now.timeZoneId())
              .toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              })}
          </time>
          .
        </p>
      )}

      {!status.acceptance && !status.canAccept && (
        <p className="text-sm text-muted-foreground">
          Ask an organization administrator to accept these terms on behalf of{" "}
          {status.organizationName}.
        </p>
      )}

      {!status.acceptance && status.canAccept && (
        <form onSubmit={acceptTerms} className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id={checkboxId}
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked === true)}
              disabled={acceptMutation.isPending}
            />
            <Label htmlFor={checkboxId} className="text-sm leading-relaxed">
              {agreement.acceptanceStatement}
            </Label>
          </div>
          {acceptMutation.isError && (
            <p role="alert" className="text-sm text-destructive">
              {acceptMutation.error.message ||
                "We couldn't save acceptance. Please try again."}
            </p>
          )}
          <Button
            type="submit"
            disabled={!confirmed || acceptMutation.isPending}
          >
            {acceptMutation.isPending ? "Saving acceptance…" : "Accept terms"}
          </Button>
        </form>
      )}
    </div>
  );
}
